import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES, STORAGE_KEY } from "@/i18n";
import { useAuth } from "@/contexts/AuthContext";
import { useDirection } from "@/contexts/DirectionContext";
import api from "@/lib/api";

/**
 * LanguageContext — user-facing language state.
 *
 *  • Reads localStorage on mount so the choice loads instantly.
 *  • When the signed-in user's server profile has a stored `language`
 *    that differs from localStorage, the server value wins (so the
 *    preference follows across devices).
 *  • Changing the language:
 *      1. flips i18next
 *      2. writes localStorage
 *      3. persists to the server via PUT /auth/settings
 *      4. auto-flips layout direction to match the language (users can
 *         still override manually via Settings → Layout direction).
 */

const LanguageContext = createContext(null);

const LANG_TO_DIR = Object.fromEntries(SUPPORTED_LANGUAGES.map((l) => [l.code, l.dir]));

export function LanguageProvider({ children }) {
  const { i18n } = useTranslation();
  const { user, setUser } = useAuth();
  const { setPreference: setDirection } = useDirection();
  const [language, setLanguageState] = useState(() => i18n.language?.split("-")[0] || "en");

  // Sync from the server profile when it loads / changes
  useEffect(() => {
    const serverLang = user?.language;
    if (!serverLang || serverLang === language) return;
    if (!SUPPORTED_LANGUAGES.some((l) => l.code === serverLang)) return;
    setLanguageState(serverLang);
    i18n.changeLanguage(serverLang);
    try { localStorage.setItem(STORAGE_KEY, serverLang); } catch { /* ignore */ }
    // Server-driven language change also flips direction
    setDirection(LANG_TO_DIR[serverLang] || "ltr");
  }, [user?.language, language, i18n, setDirection]);

  // Update <html lang> whenever the language changes (dir is owned by
  // DirectionContext so we don't touch it here).
  useEffect(() => {
    document.documentElement.setAttribute("lang", language);
  }, [language]);

  const setLanguage = useCallback(async (code) => {
    if (!SUPPORTED_LANGUAGES.some((l) => l.code === code)) return;
    setLanguageState(code);
    i18n.changeLanguage(code);
    try { localStorage.setItem(STORAGE_KEY, code); } catch { /* ignore */ }
    // Auto-flip direction to match the language's default. Users can
    // change it back via Settings → Layout direction if they prefer.
    setDirection(LANG_TO_DIR[code] || "ltr");
    // Persist to server if signed in (silent — UI reacts to the local
    // change immediately; server sync is best-effort).
    if (user) {
      try {
        const { data } = await api.put("/auth/settings", { language: code });
        setUser(data);
      } catch (e) {
        // Non-fatal — the local choice already applied
        console.warn("Language persist failed:", e);
      }
    }
  }, [i18n, user, setUser, setDirection]);

  return (
    <LanguageContext.Provider value={useMemo(
      () => ({ language, setLanguage, languages: SUPPORTED_LANGUAGES }),
      [language, setLanguage],
    )}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used inside <LanguageProvider>");
  return ctx;
};
